import locale from "antd/lib/locale/es_ES";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { SearchOutlined, MinusCircleTwoTone, PlusCircleTwoTone } from "@ant-design/icons";
import { Button, Col, Row, Space, Table, DatePicker, ConfigProvider, Input, Form, Select } from "antd";

import "moment/locale/es-mx";

import {
  useTaxRetentions,
  useTaxRetentionFile,
  UseTaxRetentionFileProps,
} from "services/tax-retentions.service";

const { RangePicker } = DatePicker;

import type { ItfTaxpayer } from "types/taxpayers";
import type { ItfFiscalDoc, ItfSearchTaxRetentions, StatusModeFiscalDoc } from "types/taxes";

let mock = {
  data: [
    {
        "penalized": false,
        "uuid": "ffff61ea-a0aa-42d3-bb91-5f7508647bda",
        "partner_name": "Rappi Certification",
        "total_amount": 875.28,
        "total_witholding": "67.84",
        "govt_tax": "0.00",
        "emitted_at": "2025-03-05T03:23:30.033Z",
        "state": "GENERATED",
        "pdf_url": "https://d2v7arnotajv51.cloudfront.net/rappi/certification/output/cfdi/ffff61ea-a0aa-42d3-bb91-5f7508647bda.pdf",
        "xml_url": "https://d2v7arnotajv51.cloudfront.net/rappi/certification/output/cfdi/FFFF61EA-A0AA-42D3-BB91-5F7508647BDA.xml",
        "period": "2025-02"
    }
]
}

interface Props {
  itemsPerPage?: number;
  defaultStatus?: StatusModeFiscalDoc | "";
  statusFilter?: { text: string; value: string }[];
  taxpayer?: ItfTaxpayer | null;
  showPeriodicity: boolean;
  partnerId?: number;
}

export function TaxRetentions(props: Props): JSX.Element {
  const {
    taxpayer = null,
    // statusFilter = [],
    itemsPerPage = 50,
    defaultStatus = "",
    showPeriodicity = true,
  } = props || {};
  const initQueryDocFile: UseTaxRetentionFileProps = { docId: 0, type: "pdf", options: { enabled: false } };
  const initFilters = { status: defaultStatus, period_from: "", uuid: "" };
  const defaultEmissionRange = (() => {
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 7);
    return { from: fmt(from), to: fmt(to) };
  })();
  const taxRetentionInit: ItfSearchTaxRetentions = {
    page: 1,
    limit: itemsPerPage,
    state: null,
    year: null,
    month: null,
    penalized: false,
    rfc: taxpayer?.rfc,
    uuid: null,
    partnerId: props.partnerId,
    emission_date_from: defaultEmissionRange.from,
    emission_date_to: defaultEmissionRange.to,
  };

  // const breakpoints = Grid.useBreakpoint();
  const [searchFilters, setSearchFilters] = useState(initFilters);
  const [rfc, setRfc] = useState(null);
  const [uuid, setUuid] = useState(null);
  const [status, setStatus] = useState(null);
  const [penalize, setPenalize] = useState(null);
  const [loadingRetention, setLoading] = useState(false);
  const [year, setYear] = useState(taxRetentionInit.year);
  const [month, setMonth] = useState(taxRetentionInit.month);
  const [emissionDateFrom, setEmissionDateFrom] = useState<string | null>(defaultEmissionRange.from);
  const [emissionDateTo, setEmissionDateTo] = useState<string | null>(defaultEmissionRange.to);
  const [retentions, setRetentions] = useState(taxRetentionInit);
  const [queryDocFile, setQueryDocFile] = useState(initQueryDocFile);

  const retentionFile = useTaxRetentionFile(queryDocFile);

  const taxRetentions = useTaxRetentions({ filters: retentions });

  const showMonth = (date) => {
    if (!date) {
      setMonth(null);
      setYear(null);
      return;
    }
    setMonth(`${date.month() + 1}`);
    setYear(`${date.year()}`);
  };

  const handleEmissionDateRange = (_dates, dateStrings: [string, string]) => {
    setEmissionDateFrom(dateStrings?.[0] || null);
    setEmissionDateTo(dateStrings?.[1] || null);
  };

  const fetchRetentions = () => {
    setRetentions({
      page: 1,
      limit: itemsPerPage,
      state: status,
      penalized: penalize === "true",
      month: month,
      rfc: rfc,
      uuid: uuid,
      year: year,
      partnerId: props.partnerId,
      emission_date_from: emissionDateFrom,
      emission_date_to: emissionDateTo,
    });
  };

  const cleanSearch = () => {
    window.location.reload();
  };

  const SearchButton = () => (
    <Button type="primary" icon={<SearchOutlined />} onClick={fetchRetentions} loading={taxRetentions.isLoading || taxRetentions.isFetching}>
      Buscar
    </Button>
  );

  const CleanButton = () => (
    <Button type="primary" ghost onClick={cleanSearch}>
      Limpiar
    </Button>
  );

  /* const cancelRetention = useCancelRetention({
    rqOptions: {
      onSuccess: (rData: any): void => {
        notification.success({ message: `Se ha cancelado la retención (ID: ${rData.docId})`, duration: 5 });
        (async () => await queryClient.invalidateQueries(qkBaseTaxRetentions(), { refetchActive: true }))();
      },
      onError: (rError: any): void => {
        notification.error({ message: "Falló al cancelar la retención", description: JSON.stringify(rError.message) });
      },
    },
  }); */

  const handleFilterValue = useCallback(
    ({ value, dataIndex }): void => {
      setTimeout(() => setSearchFilters((ps) => ({ ...ps, [dataIndex]: value })), 20);
    },
    [itemsPerPage],
  );

  /* const handleDownloadPdfXml = (params: { docId: number; type: TypesToDownloadRetentionFile }): void => {
    const { docId, type = "pdf" } = params;
    setQueryDocFile({ docId, type, rqOptions: { enabled: true } });
  };

  const handleCancelFiscalDoc = ({ docId }: { docId: number }): void => cancelRetention.mutate({ docId }); */

  function handlePagination(page: number, pageSize?: number) {
    setRetentions((prev) => ({
      ...prev,
      page,
      limit: pageSize ?? prev.limit ?? itemsPerPage,
      state: status,
      penalized: penalize,
      month: month,
      rfc: rfc,
      uuid: uuid,
      year: year,
      partnerId: props.partnerId,
      emission_date_from: emissionDateFrom,
      emission_date_to: emissionDateTo,
    }));
  }

  useEffect((): void => {
    if (queryDocFile.options.enabled && (retentionFile.isSuccess || retentionFile.isError)) {
      if (retentionFile.isSuccess) window.open(retentionFile.data.url);
      setQueryDocFile((ps) => ({ ...ps, options: { enabled: false } }));
    }
  }, [queryDocFile, retentionFile.status]);

  return (
    <div style={{ display: "flex", flex: 1, flexDirection: "column", width: "100%" }}>
      {showPeriodicity && (
        <Row style={{ backgroundColor: "#FFF", padding: "1.5rem 2%", marginBottom: "1rem", borderRadius: 6 }}>
          <Form
            name="historicRetentions"
            autoComplete="off"
            layout="vertical"
            style={{ width: "100%" }}
          >
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={12} md={12} lg={8}>
                <Form.Item
                  name="emissionDateRange"
                  label="Fecha de emisión"
                  tooltip="Rango de fechas en que se emitió el certificado"
                >
                  <ConfigProvider locale={locale}>
                    <RangePicker
                      format="YYYY-MM-DD"
                      onChange={handleEmissionDateRange}
                      defaultValue={[
                        emissionDateFrom ? moment(emissionDateFrom) : null,
                        emissionDateTo ? moment(emissionDateTo) : null,
                      ]}
                      placeholder={["Desde", "Hasta"]}
                      style={{ width: "100%" }}
                    />
                  </ConfigProvider>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item
                  name="periodicity"
                  label="Periodo de emisión"
                  tooltip="Mes y año al que corresponde el certificado"
                >
                  <ConfigProvider locale={locale}>
                    <DatePicker
                      picker="month"
                      format="MMMM YYYY"
                      onChange={showMonth}
                      placeholder="Selecciona el mes"
                      style={{ width: "100%" }}
                      allowClear
                    />
                  </ConfigProvider>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label="RFC" name="rfc">
                  <Input
                    placeholder="RFC del contribuyente"
                    onChange={(e) => setRfc(e.target.value.toUpperCase())}
                    value={rfc?.toUpperCase()}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label="UUID" name="uuid">
                  <Input
                    placeholder="UUID del certificado"
                    onChange={(e) => setUuid(e.target.value)}
                    value={uuid}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label="Estado del certificado" name="statusCertificate">
                  <Select placeholder="Selecciona un estado" onChange={(value) => setStatus(value)} value={status} allowClear>
                    <Select.Option value="FAILED">Fallido</Select.Option>
                    <Select.Option value="GENERATED">Vigente</Select.Option>
                    <Select.Option value="CREATED">Creado (En proceso)</Select.Option>
                    <Select.Option value="CANCELED">Cancelado</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label="Penalizado" name="penalized">
                  <Select placeholder="Sí / No" onChange={(value) => setPenalize(value)} value={penalize} allowClear>
                    <Select.Option value="true">SI</Select.Option>
                    <Select.Option value="false">NO</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label="Tipo" name="type">
                  <Select placeholder="Selecciona un tipo" onChange={(value) => setPenalize(value)} value={penalize} allowClear>
                    <Select.Option value="rts">RTs</Select.Option>
                    <Select.Option value="allies">Aliados</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row justify="end">
              <Space direction="horizontal">
                <CleanButton />
                <SearchButton />
              </Space>
            </Row>
          </Form>
        </Row>
      )}
      <Table
        bordered
        scroll={{ x: true }}
        loading={{ spinning: taxRetentions.isLoading || taxRetentions.isFetching, size: "small" }}
        locale={{ emptyText: "Sin resultados" }}
        dataSource={(Array.isArray(taxRetentions.data?.data)
          ? taxRetentions.data.data
          : Array.isArray(taxRetentions.data) ? taxRetentions.data : []
        ).map((item, idx) => ({
          ...item,
          key: item?.uuid ?? `${item?.id ?? "row"}-${idx}`,
          status: item?.state,
          period: item?.period,
          issued_at: item?.emitted_at,
          total_service: item?.total_amount,
          total_retention: item?.total_witholding,
          tax_governmental: item?.govt_tax,
          penalized: item?.penalized ? "SÍ" : "NO",
          uuid: item?.uuid,
        }))}
        pagination={{
          current: retentions.page,
          pageSize: retentions.limit,
          total: taxRetentions.data?.total ?? 0,
          showSizeChanger: false,
          onChange: handlePagination,
          hideOnSinglePage: true,
        }}
        columns={[
          {
            dataIndex: "id",
            title: "ID",
            align: "right",
            key: "id",
          },
          {
            dataIndex: "status",
            title: "Estatus",
            key: "status",
          },
          {
            dataIndex: "period",
            title: "Periodo",
            key: "period",
            // render: renderContentDatetime001,
          },
          {
            dataIndex: "issued_at",
            title: "Fecha de emisión",
            key: "issued_at",
          },
          {
            dataIndex: "total_service",
            title: "Total de Servicio",
            key: "total_service",
          },
          {
            dataIndex: "total_retention",
            title: "Total Retenido",
            key: "total_retention",
          },
          {
            dataIndex: "tax_governmental",
            title: "Impuesto Gubernamental",
            key: "tax_governmental",
          },
          {
            dataIndex: "penalized",
            title: "Penalizado",
            key: "penalized",
          },
          {
            dataIndex: "uuid",
            title: "UUID",
            key: "uuid",
            /* ...customSearchFilterToTableAntD({
              mode: "alphanumeric",
              isLoading: taxRetentions.isLoading,
              isActiveFilter: !!searchFilters["uuid"],
              onSearch: (search) => handleFilterValue({ dataIndex: "uuid", value: search }),
            }), */
          },
          /* {
            dataIndex: "status",
            title: "Estado",
            ...customRadioGroupFilterToTableAntD({
              filterOptions: statusFilter,
              defaultValue: searchFilters.status,
              isActiveFilter: !!searchFilters.status,
              onChange: (value) => handleFilterValue({ dataIndex: "status", value }),
            }),
          }, */
          /* {
            dataIndex: "period_from",
            title: "Periodo Desde",
            ...customDataPickerFilterToTableAntD({
              isActiveFilter: !!searchFilters.period_from,
              onChange: (_, dateString): void => {
                handleFilterValue({
                  dataIndex: "period_from",
                  value: dateString ? dateString + "T00:00:00" : undefined,
                });
              },
            }),
          }, */
        ]}
        expandable={{
          expandIcon: ({ expanded, onExpand, record }) =>
            expanded ? (
              <MinusCircleTwoTone onClick={(e) => onExpand(record, e)} />
            ) : (
              <PlusCircleTwoTone onClick={(e) => onExpand(record, e)} />
            ),
          expandedRowRender: (record: ItfFiscalDoc) => {
            return (
              <Row>
                <Col xs={{ offset: 1, span: 22 }} md={{ offset: 1, span: 10 }} style={{ marginBottom: "1.5rem" }}>
                  <Space size="large">
                    <Button
                      onClick={() => {
                        if (record.pdf_url) {
                          window.open(record.pdf_url, "_blank");
                        } else {
                          alert("No esta generado el documento, pide ayuda a soporte.");
                        }
                      }}
                    >
                      Descargar PDF
                    </Button>
                    <Button
                      onClick={() => {
                        if (record.xml_url) {
                          window.open(record.xml_url, "_blank");
                        } else {
                          alert("No esta generado el documento, pide ayuda a soporte.");
                        }
                      }}
                    >
                      Descargar XML
                    </Button>
                  </Space>
                </Col>
                {/* <Col xs={{ offset: 1, span: 22 }} md={{ offset: 2, span: 10 }}>
                <Row justify={breakpoints.md ? "end" : "start"}>
                  <Popconfirm
                    okType="danger"
                    title="¿Estás seguro de querer cancelar esta retención?"
                    okText={<>&nbsp; Sí &nbsp;</>}
                    cancelText={<>&nbsp; No &nbsp;</>}
                    onConfirm={() => handleCancelFiscalDoc({ docId: record.id })}
                    disabled={record.status === "CANCELLED"}
                  >
                    <Button danger={true} disabled={record.status === "CANCELLED"}>
                      Cancelar Certificado
                    </Button>
                  </Popconfirm>
                </Row>
              </Col> */}
              </Row>
            );
          },
        }}
      />
    </div>
  );
}
